import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { resolveFrontendApiBase } from '../utils/apiBase';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import adminAxios from './adminAxios';
import './AdminDashboardSurfaces.css';

const { Title, Text } = Typography;
const { Option } = Select;
const API_BASE = resolveFrontendApiBase();
const RELIABILITY_CHART_COLORS = {
  success: 'var(--accent-success)',
  danger: 'var(--accent-danger)',
  warning: 'var(--accent-warning)',
  info: 'var(--accent-primary)',
  teal: 'var(--text-info)',
  grid: 'var(--border-subtle)',
};

type SummaryPayload = {
  summary: {
    eventCount: number;
    terminalAttemptCount: number;
    successCount: number;
    failureCount: number;
    redoCount: number;
    refusalCount?: number;
    retryScheduledCount: number;
    retryExhaustedCount: number;
    estimatedCostUsd: number;
    retryCostUsd: number;
    failureLikeCostUsd: number;
    proofreaderRetryScheduledCount: number;
    failureRate: number;
    refusalRate?: number;
    alignmentRedoRate: number;
    alignmentPostTranslateRedoRate?: number;
    alignmentFinalRedoRate?: number;
    proofreaderRetryRate: number;
    retryRecoveryRate: number;
    retryCostShare: number;
  };
  filters: {
    sourceLangs: string[];
    targetLangs: string[];
    stages: string[];
    outcomes: string[];
    outcomeCodes: string[];
    providers: string[];
    models: string[];
    workflows: string[];
  };
  metric_definitions?: Record<string, string>;
};

type TrendItem = {
  day: string;
  successCount: number;
  failureCount: number;
  redoCount: number;
  refusalCount?: number;
  retryScheduledCount: number;
  refusalRate?: number;
  alignmentRedoRate?: number;
  alignmentPostTranslateRedoRate?: number;
  alignmentFinalRedoRate?: number;
  proofreaderRetryRate?: number;
};

type BreakdownPayload = {
  byStage: Array<{
    stage: string;
    eventCount: number;
    terminalAttemptCount: number;
    failureCount: number;
    successCount: number;
    redoCount: number;
    refusalCount: number;
    retryScheduledCount: number;
    retryExhaustedCount: number;
    failureRate: number;
    estimatedCostUsd: number;
  }>;
  byModelLanguage: Array<{
    configuredModelId: string;
    provider: string;
    sourceLang: string;
    targetLang: string;
    eventCount: number;
    terminalAttemptCount: number;
    failureCount: number;
    successCount: number;
    redoCount: number;
    refusalCount: number;
    failureRate: number;
    estimatedCostUsd: number;
  }>;
  byReason: Array<{
    outcomeCode: string;
    count: number;
    failureLikeCount: number;
    estimatedCostUsd: number;
  }>;
  customerImpact?: {
    impactedUserCount: number;
    impactedBookCount: number;
    impactedChapterCount: number;
    impactedRunOrChapterCount: number;
    chapterFinalizeTerminalCount: number;
    chapterFinalizeFailureCount: number;
    chapterFinalizeFailureRate: number;
    retryExhaustedCount: number;
    retryExhaustedUserCount: number;
    usersWithRepeatedFinalizeFailures: number;
    topImpactedUsers: Array<{
      userId: string;
      chapterFinalizeFailures: number;
      retryExhaustedCount: number;
      affectedBooks: number;
      affectedChapters: number;
      estimatedCostUsd: number;
    }>;
    topImpactedBooks: Array<{
      bookId: string;
      chapterFinalizeFailures: number;
      retryExhaustedCount: number;
      affectedUsers: number;
      affectedChapters: number;
      estimatedCostUsd: number;
    }>;
  };
  failureFunnel?: {
    trackedRunOrChapterCount: number;
    alignmentRedoCount: number;
    alignmentFinalRedoCount: number;
    retryExhaustedCount: number;
    chapterFinalizeFailureCount: number;
    chapterFinalizeAlignmentFailureCount: number;
    chapterFinalizeProviderRejectedCount: number;
    chapterFinalizeSuccessCount: number;
    recoveredAfterAlignmentRedoCount: number;
    retryExhaustedWithoutSuccessCount: number;
    retryExhaustedThenSuccessCount: number;
    finalizeFailureThenSuccessCount: number;
    alignmentRedoRate: number;
    retryExhaustedRate: number;
    chapterFinalizeFailureRate: number;
    recoveredAfterAlignmentRedoRate: number;
  };
  byReasonBucket?: Array<{
    bucket: string;
    eventCount: number;
    failureLikeCount: number;
    estimatedCostUsd: number;
    topOutcomeCodes: Array<{
      outcomeCode: string;
      count: number;
    }>;
  }>;
  modelOutliers?: Array<{
    configuredModelId: string;
    provider: string;
    sourceLang: string;
    targetLang: string;
    terminalAttemptCount: number;
    failureCount: number;
    failureRate: number;
    globalFailureRate: number;
    deltaFailureRate: number;
    estimatedCostUsd: number;
    severity: string;
  }>;
  falsePositiveSignals?: {
    trackedRunOrChapterCount: number;
    alignmentRedoRecoveredCount: number;
    finalizeFailureRecoveredCount: number;
    retryExhaustedRecoveredCount: number;
    thinReasonTextCount: number;
    failureLikeEventCount: number;
    thinReasonTextRate: number;
  };
  regressionAlerts?: Array<{
    metric: string;
    label: string;
    baseline: number;
    recent: number;
    delta: number;
    recentWindowDays: number;
    baselineWindowDays: number;
    severity: string;
    latestDay: string;
    message: string;
  }>;
  chapterHotspots?: Array<{
    bookId: string;
    chapterGuid: string;
    sourceLang: string;
    targetLang: string;
    eventCount: number;
    failureLikeCount: number;
    redoCount: number;
    retryExhaustedCount: number;
    chapterFinalizeFailureCount: number;
    affectedUsers: number;
    estimatedCostUsd: number;
  }>;
  runHotspots?: Array<{
    runId: string;
    bookId: string;
    chapterGuid: string;
    sourceLang: string;
    targetLang: string;
    eventCount: number;
    failureLikeCount: number;
    redoCount: number;
    retryExhaustedCount: number;
    chapterFinalizeFailureCount: number;
    estimatedCostUsd: number;
  }>;
};

type EventItem = {
  id?: string;
  timestamp?: string;
  stage?: string;
  outcome?: string;
  outcomeCode?: string;
  reasonText?: string;
  bookId?: string;
  chapterGuid?: string;
  chunkId?: number;
  runId?: string;
  userId?: string;
  sourceLang?: string;
  targetLang?: string;
  provider?: string;
  configuredModelId?: string;
  attemptNumber?: number;
  estimatedCostUsd?: number;
};

type EventsPayload = {
  items: EventItem[];
};

type DeleteOutcomesResponse = {
  deleted_events: number;
  deleted_daily_aggregates: number;
  deleted_rebuild_jobs: number;
};

type FiltersState = {
  days: number;
  source_lang?: string;
  target_lang?: string;
  stage?: string;
  outcome?: string;
  outcome_code?: string;
  provider?: string;
  model?: string;
  book_id?: string;
  chapter_guid?: string;
  run_id?: string;
  workflow: string;
};

const percent = (value?: number) => `${((value || 0) * 100).toFixed(2)}%`;
const usd = (value?: number) => `$${(value || 0).toFixed(4)}`;
const compareText = (left?: string, right?: string) =>
  (left || '').localeCompare(right || '', undefined, { sensitivity: 'base', numeric: true });
const compareNumber = (left?: number, right?: number) => Number(left || 0) - Number(right || 0);
const compareDate = (left?: string, right?: string) => {
  const leftMs = left ? new Date(left).getTime() : 0;
  const rightMs = right ? new Date(right).getTime() : 0;
  return (Number.isFinite(leftMs) ? leftMs : 0) - (Number.isFinite(rightMs) ? rightMs : 0);
};

const TranslationReliabilityTab: React.FC = () => {
  const [filters, setFilters] = useState<FiltersState>({
    days: 7,
    workflow: 'single',
  });
  const [summary, setSummary] = useState<SummaryPayload['summary'] | null>(null);
  const [filterOptions, setFilterOptions] = useState<SummaryPayload['filters'] | null>(null);
  const [metricDefinitions, setMetricDefinitions] = useState<Record<string, string>>({});
  const [trends, setTrends] = useState<TrendItem[]>([]);
  const [breakdown, setBreakdown] = useState<BreakdownPayload | null>(null);
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [rebuilding, setRebuilding] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const [timelineEvents, setTimelineEvents] = useState<EventItem[]>([]);
  const [timelineLoading, setTimelineLoading] = useState<boolean>(false);
  const [dashboardNotice, setDashboardNotice] = useState<string | null>(null);
  const dashboardRequestIdRef = useRef(0);

  const requestParams = useMemo(() => {
    const params: Record<string, string | number> = {
      days: filters.days,
      workflow: filters.workflow,
    };
    if (filters.source_lang) params.source_lang = filters.source_lang;
    if (filters.target_lang) params.target_lang = filters.target_lang;
    if (filters.stage) params.stage = filters.stage;
    if (filters.outcome) params.outcome = filters.outcome;
    if (filters.outcome_code) params.outcome_code = filters.outcome_code;
    if (filters.provider) params.provider = filters.provider;
    if (filters.model) params.model = filters.model;
    const runId = filters.run_id?.trim();
    const bookId = filters.book_id?.trim();
    const chapterGuid = filters.chapter_guid?.trim();
    if (bookId) params.book_id = bookId;
    if (chapterGuid) params.chapter_guid = chapterGuid;
    if (runId) params.run_id = runId;
    return params;
  }, [filters]);

  const loadDashboard = useCallback(async () => {
    const requestId = dashboardRequestIdRef.current + 1;
    dashboardRequestIdRef.current = requestId;
    setLoading(true);
    setDashboardNotice(null);
    const failedSections: string[] = [];
    const isCurrentRequest = () => dashboardRequestIdRef.current === requestId;

    const loadSection = async <T,>(
      sectionName: string,
      requestFn: () => Promise<T>,
      onSuccess: (payload: T) => void,
      onFailure: () => void,
    ) => {
      try {
        const payload = await requestFn();
        if (!isCurrentRequest()) return;
        onSuccess(payload);
      } catch {
        if (!isCurrentRequest()) return;
        failedSections.push(sectionName);
        onFailure();
      }
    };

    try {
      await loadSection(
        'summary',
        async () => {
          const response = await adminAxios.get<SummaryPayload>(`${API_BASE}/admin/translation-outcomes/summary`, {
            params: requestParams,
          });
          return response.data;
        },
        (data) => {
          setSummary(data.summary);
          setFilterOptions(data.filters);
          setMetricDefinitions(data.metric_definitions || {});
        },
        () => {
          setSummary(null);
          setFilterOptions(null);
          setMetricDefinitions({});
        },
      );

      await loadSection(
        'trends',
        async () => {
          const response = await adminAxios.get<{ items: TrendItem[] }>(`${API_BASE}/admin/translation-outcomes/trends`, {
            params: requestParams,
          });
          return response.data;
        },
        (data) => {
          setTrends(Array.isArray(data.items) ? data.items : []);
        },
        () => {
          setTrends([]);
        },
      );

      await loadSection(
        'breakdown',
        async () => {
          const response = await adminAxios.get<BreakdownPayload>(`${API_BASE}/admin/translation-outcomes/breakdown`, {
            params: requestParams,
          });
          return response.data;
        },
        (data) => {
          setBreakdown(data);
        },
        () => {
          setBreakdown(null);
        },
      );

      await loadSection(
        'events',
        async () => {
          const response = await adminAxios.get<EventsPayload>(`${API_BASE}/admin/translation-outcomes/events`, {
            params: {
              ...requestParams,
              limit: 100,
              offset: 0,
            },
          });
          return response.data;
        },
        (data) => {
          setEvents(Array.isArray(data.items) ? data.items : []);
        },
        () => {
          setEvents([]);
        },
      );

      if (!isCurrentRequest()) return;
      if (failedSections.length === 4) {
        setDashboardNotice('All reliability sections failed to load. Existing data may be stale or incomplete.');
        message.error('Failed to load translation reliability dashboard');
      } else if (failedSections.length > 0) {
        setDashboardNotice(`Some sections failed to load: ${failedSections.join(', ')}. Existing data may be stale or incomplete.`);
        message.warning(`Some sections failed to load: ${failedSections.join(', ')}`);
      }
    } finally {
      if (isCurrentRequest()) {
        setLoading(false);
      }
    }
  }, [requestParams]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      loadDashboard();
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [loadDashboard]);

  const handleRebuild = useCallback(async () => {
    setRebuilding(true);
    try {
      const res = await adminAxios.post<{ job_id: string }>(`${API_BASE}/admin/translation-outcomes/rebuild`, {
        days: filters.days,
        force: true,
      });
      message.success(`Rebuild queued: ${res.data.job_id}`);
    } catch {
      message.error('Failed to queue rebuild');
    } finally {
      setRebuilding(false);
    }
  }, [filters.days]);

  const handleDeleteEvents = useCallback(async () => {
    setDeleting(true);
    try {
      const res = await adminAxios.post<DeleteOutcomesResponse>(`${API_BASE}/admin/translation-outcomes/delete`, {
        delete_daily_aggregates: true,
        delete_rebuild_jobs: true,
      });
      const deletedEvents = Number(res.data.deleted_events || 0);
      const deletedDaily = Number(res.data.deleted_daily_aggregates || 0);
      const deletedJobs = Number(res.data.deleted_rebuild_jobs || 0);
      message.success(
        `Deleted ${deletedEvents} events, ${deletedDaily} daily rows, ${deletedJobs} rebuild records`,
      );
      setSelectedEvent(null);
      await loadDashboard();
    } catch {
      message.error('Failed to delete translation outcome data');
    } finally {
      setDeleting(false);
    }
  }, [loadDashboard]);

  const applyRunFilter = useCallback((runId?: string) => {
    if (!runId) return;
    setFilters((prev) => ({
      ...prev,
      run_id: runId,
      book_id: undefined,
      chapter_guid: undefined,
    }));
    setSelectedEvent(null);
  }, []);

  const applyChapterFilter = useCallback((bookId?: string, chapterGuid?: string) => {
    if (!bookId || !chapterGuid) return;
    setFilters((prev) => ({
      ...prev,
      run_id: undefined,
      book_id: bookId,
      chapter_guid: chapterGuid,
    }));
    setSelectedEvent(null);
  }, []);

  const clearDrilldownFilters = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      run_id: undefined,
      book_id: undefined,
      chapter_guid: undefined,
    }));
  }, []);

  useEffect(() => {
    const fetchTimeline = async () => {
      if (!selectedEvent) {
        setTimelineEvents([]);
        return;
      }

      const runId = selectedEvent.runId;
      const bookId = selectedEvent.bookId;
      const chapterGuid = selectedEvent.chapterGuid;
      if (!runId && !(bookId && chapterGuid)) {
        setTimelineEvents([]);
        return;
      }

      setTimelineLoading(true);
      try {
        const params: Record<string, string | number> = {
          days: filters.days,
          workflow: filters.workflow,
          limit: 250,
          offset: 0,
        };
        if (runId) {
          params.run_id = runId;
        } else if (bookId && chapterGuid) {
          params.book_id = bookId;
          params.chapter_guid = chapterGuid;
        }

        const res = await adminAxios.get<EventsPayload>(`${API_BASE}/admin/translation-outcomes/events`, { params });
        const timeline = Array.isArray(res.data.items) ? res.data.items : [];
        timeline.sort((left, right) => compareDate(left.timestamp, right.timestamp));
        setTimelineEvents(timeline);
      } catch {
        setTimelineEvents([]);
      } finally {
        setTimelineLoading(false);
      }
    };

    fetchTimeline();
  }, [selectedEvent, filters.days, filters.workflow]);

  const trendRateSeries = useMemo(
    () =>
      trends.map((item) => ({
        day: item.day,
        alignmentPostTranslateRedoRatePct: (
          item.alignmentPostTranslateRedoRate
          ?? item.alignmentRedoRate
          ?? 0
        ) * 100,
        alignmentFinalRedoRatePct: (
          item.alignmentFinalRedoRate
          ?? item.alignmentRedoRate
          ?? 0
        ) * 100,
        proofreaderRetryRatePct: (item.proofreaderRetryRate || 0) * 100,
      })),
    [trends],
  );

  const stageColumns: ColumnsType<BreakdownPayload['byStage'][number]> = [
    {
      title: 'Stage',
      dataIndex: 'stage',
      key: 'stage',
      sorter: (a, b) => compareText(a.stage, b.stage),
    },
    {
      title: 'Events',
      dataIndex: 'eventCount',
      key: 'eventCount',
      sorter: (a, b) => compareNumber(a.eventCount, b.eventCount),
    },
    {
      title: 'Terminal',
      dataIndex: 'terminalAttemptCount',
      key: 'terminalAttemptCount',
      sorter: (a, b) => compareNumber(a.terminalAttemptCount, b.terminalAttemptCount),
    },
    {
      title: 'Failures',
      dataIndex: 'failureCount',
      key: 'failureCount',
      sorter: (a, b) => compareNumber(a.failureCount, b.failureCount),
    },
    {
      title: 'REDO',
      dataIndex: 'redoCount',
      key: 'redoCount',
      sorter: (a, b) => compareNumber(a.redoCount, b.redoCount),
    },
    {
      title: 'Failure Rate',
      dataIndex: 'failureRate',
      key: 'failureRate',
      render: (v) => percent(v),
      sorter: (a, b) => compareNumber(a.failureRate, b.failureRate),
    },
    {
      title: 'Est. Cost',
      dataIndex: 'estimatedCostUsd',
      key: 'estimatedCostUsd',
      render: (v) => usd(v),
      sorter: (a, b) => compareNumber(a.estimatedCostUsd, b.estimatedCostUsd),
    },
  ];

  const modelColumns: ColumnsType<BreakdownPayload['byModelLanguage'][number]> = [
    {
      title: 'Model',
      dataIndex: 'configuredModelId',
      key: 'configuredModelId',
      sorter: (a, b) => compareText(a.configuredModelId, b.configuredModelId),
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      sorter: (a, b) => compareText(a.provider, b.provider),
    },
    {
      title: 'Pair',
      key: 'pair',
      render: (_, row) => `${row.sourceLang || '—'} -> ${row.targetLang || '—'}`,
      sorter: (a, b) =>
        compareText(`${a.sourceLang || ''}->${a.targetLang || ''}`, `${b.sourceLang || ''}->${b.targetLang || ''}`),
    },
    {
      title: 'Events',
      dataIndex: 'eventCount',
      key: 'eventCount',
      sorter: (a, b) => compareNumber(a.eventCount, b.eventCount),
    },
    {
      title: 'Failures',
      dataIndex: 'failureCount',
      key: 'failureCount',
      sorter: (a, b) => compareNumber(a.failureCount, b.failureCount),
    },
    {
      title: 'Failure Rate',
      dataIndex: 'failureRate',
      key: 'failureRate',
      render: (v) => percent(v),
      sorter: (a, b) => compareNumber(a.failureRate, b.failureRate),
    },
    {
      title: 'Est. Cost',
      dataIndex: 'estimatedCostUsd',
      key: 'estimatedCostUsd',
      render: (v) => usd(v),
      sorter: (a, b) => compareNumber(a.estimatedCostUsd, b.estimatedCostUsd),
    },
  ];

  const reasonColumns: ColumnsType<BreakdownPayload['byReason'][number]> = [
    {
      title: 'Outcome Code',
      dataIndex: 'outcomeCode',
      key: 'outcomeCode',
      sorter: (a, b) => compareText(a.outcomeCode, b.outcomeCode),
    },
    {
      title: 'Count',
      dataIndex: 'count',
      key: 'count',
      sorter: (a, b) => compareNumber(a.count, b.count),
    },
    {
      title: 'Failure-like',
      dataIndex: 'failureLikeCount',
      key: 'failureLikeCount',
      sorter: (a, b) => compareNumber(a.failureLikeCount, b.failureLikeCount),
    },
    {
      title: 'Est. Cost',
      dataIndex: 'estimatedCostUsd',
      key: 'estimatedCostUsd',
      render: (v) => usd(v),
      sorter: (a, b) => compareNumber(a.estimatedCostUsd, b.estimatedCostUsd),
    },
  ];

  const reasonBucketColumns: ColumnsType<NonNullable<BreakdownPayload['byReasonBucket']>[number]> = [
    {
      title: 'Bucket',
      dataIndex: 'bucket',
      key: 'bucket',
      sorter: (a, b) => compareText(a.bucket, b.bucket),
    },
    {
      title: 'Events',
      dataIndex: 'eventCount',
      key: 'eventCount',
      sorter: (a, b) => compareNumber(a.eventCount, b.eventCount),
    },
    {
      title: 'Failure-like',
      dataIndex: 'failureLikeCount',
      key: 'failureLikeCount',
      sorter: (a, b) => compareNumber(a.failureLikeCount, b.failureLikeCount),
    },
    {
      title: 'Top Codes',
      key: 'topOutcomeCodes',
      render: (_, row) =>
        Array.isArray(row.topOutcomeCodes) && row.topOutcomeCodes.length > 0
          ? row.topOutcomeCodes.map((item) => `${item.outcomeCode} (${item.count})`).join(', ')
          : '—',
      sorter: (a, b) =>
        compareText(
          Array.isArray(a.topOutcomeCodes) ? a.topOutcomeCodes.map((item) => item.outcomeCode).join(',') : '',
          Array.isArray(b.topOutcomeCodes) ? b.topOutcomeCodes.map((item) => item.outcomeCode).join(',') : '',
        ),
    },
    {
      title: 'Est. Cost',
      dataIndex: 'estimatedCostUsd',
      key: 'estimatedCostUsd',
      render: (v) => usd(v),
      sorter: (a, b) => compareNumber(a.estimatedCostUsd, b.estimatedCostUsd),
    },
  ];

  const modelOutlierColumns: ColumnsType<NonNullable<BreakdownPayload['modelOutliers']>[number]> = [
    {
      title: 'Model',
      dataIndex: 'configuredModelId',
      key: 'configuredModelId',
      sorter: (a, b) => compareText(a.configuredModelId, b.configuredModelId),
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      sorter: (a, b) => compareText(a.provider, b.provider),
    },
    {
      title: 'Pair',
      key: 'pair',
      render: (_, row) => `${row.sourceLang || '—'} -> ${row.targetLang || '—'}`,
      sorter: (a, b) =>
        compareText(`${a.sourceLang || ''}->${a.targetLang || ''}`, `${b.sourceLang || ''}->${b.targetLang || ''}`),
    },
    {
      title: 'Terminal',
      dataIndex: 'terminalAttemptCount',
      key: 'terminalAttemptCount',
      sorter: (a, b) => compareNumber(a.terminalAttemptCount, b.terminalAttemptCount),
    },
    {
      title: 'Failures',
      dataIndex: 'failureCount',
      key: 'failureCount',
      sorter: (a, b) => compareNumber(a.failureCount, b.failureCount),
    },
    {
      title: 'Rate',
      dataIndex: 'failureRate',
      key: 'failureRate',
      render: (v) => percent(v),
      sorter: (a, b) => compareNumber(a.failureRate, b.failureRate),
    },
    {
      title: 'Delta vs Global',
      dataIndex: 'deltaFailureRate',
      key: 'deltaFailureRate',
      render: (v) => percent(v),
      sorter: (a, b) => compareNumber(a.deltaFailureRate, b.deltaFailureRate),
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      sorter: (a, b) => compareText(a.severity, b.severity),
    },
    {
      title: 'Est. Cost',
      dataIndex: 'estimatedCostUsd',
      key: 'estimatedCostUsd',
      render: (v) => usd(v),
      sorter: (a, b) => compareNumber(a.estimatedCostUsd, b.estimatedCostUsd),
    },
  ];

  const regressionAlertColumns: ColumnsType<NonNullable<BreakdownPayload['regressionAlerts']>[number]> = [
    {
      title: 'Metric',
      dataIndex: 'label',
      key: 'label',
      sorter: (a, b) => compareText(a.label, b.label),
    },
    {
      title: 'Baseline',
      dataIndex: 'baseline',
      key: 'baseline',
      render: (v) => percent(v),
      sorter: (a, b) => compareNumber(a.baseline, b.baseline),
    },
    {
      title: 'Recent',
      dataIndex: 'recent',
      key: 'recent',
      render: (v) => percent(v),
      sorter: (a, b) => compareNumber(a.recent, b.recent),
    },
    {
      title: 'Delta',
      dataIndex: 'delta',
      key: 'delta',
      render: (v) => percent(v),
      sorter: (a, b) => compareNumber(a.delta, b.delta),
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      sorter: (a, b) => compareText(a.severity, b.severity),
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      sorter: (a, b) => compareText(a.message, b.message),
    },
  ];

  const chapterHotspotColumns: ColumnsType<NonNullable<BreakdownPayload['chapterHotspots']>[number]> = [
    {
      title: 'Book',
      dataIndex: 'bookId',
      key: 'bookId',
      sorter: (a, b) => compareText(a.bookId, b.bookId),
    },
    {
      title: 'Chapter',
      dataIndex: 'chapterGuid',
      key: 'chapterGuid',
      sorter: (a, b) => compareText(a.chapterGuid, b.chapterGuid),
    },
    {
      title: 'Pair',
      key: 'pair',
      render: (_, row) => `${row.sourceLang || '—'} -> ${row.targetLang || '—'}`,
      sorter: (a, b) =>
        compareText(`${a.sourceLang || ''}->${a.targetLang || ''}`, `${b.sourceLang || ''}->${b.targetLang || ''}`),
    },
    {
      title: 'Failures+Redo',
      dataIndex: 'failureLikeCount',
      key: 'failureLikeCount',
      sorter: (a, b) => compareNumber(a.failureLikeCount, b.failureLikeCount),
    },
    {
      title: 'REDO',
      dataIndex: 'redoCount',
      key: 'redoCount',
      sorter: (a, b) => compareNumber(a.redoCount, b.redoCount),
    },
    {
      title: 'Retry Exhausted',
      dataIndex: 'retryExhaustedCount',
      key: 'retryExhaustedCount',
      sorter: (a, b) => compareNumber(a.retryExhaustedCount, b.retryExhaustedCount),
    },
    {
      title: 'Finalize Fail',
      dataIndex: 'chapterFinalizeFailureCount',
      key: 'chapterFinalizeFailureCount',
      sorter: (a, b) => compareNumber(a.chapterFinalizeFailureCount, b.chapterFinalizeFailureCount),
    },
    {
      title: 'Users',
      dataIndex: 'affectedUsers',
      key: 'affectedUsers',
      sorter: (a, b) => compareNumber(a.affectedUsers, b.affectedUsers),
    },
    {
      title: 'Est. Cost',
      dataIndex: 'estimatedCostUsd',
      key: 'estimatedCostUsd',
      render: (v) => usd(v),
      sorter: (a, b) => compareNumber(a.estimatedCostUsd, b.estimatedCostUsd),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, row) =>
        row.bookId && row.chapterGuid ? (
          <Button size="small" onClick={() => applyChapterFilter(row.bookId, row.chapterGuid)}>
            Filter Chapter
          </Button>
        ) : null,
    },
  ];

  const runHotspotColumns: ColumnsType<NonNullable<BreakdownPayload['runHotspots']>[number]> = [
    {
      title: 'Run',
      dataIndex: 'runId',
      key: 'runId',
      ellipsis: true,
      sorter: (a, b) => compareText(a.runId, b.runId),
    },
    {
      title: 'Book',
      dataIndex: 'bookId',
      key: 'bookId',
      sorter: (a, b) => compareText(a.bookId, b.bookId),
    },
    {
      title: 'Chapter',
      dataIndex: 'chapterGuid',
      key: 'chapterGuid',
      sorter: (a, b) => compareText(a.chapterGuid, b.chapterGuid),
    },
    {
      title: 'Failures+Redo',
      dataIndex: 'failureLikeCount',
      key: 'failureLikeCount',
      sorter: (a, b) => compareNumber(a.failureLikeCount, b.failureLikeCount),
    },
    {
      title: 'REDO',
      dataIndex: 'redoCount',
      key: 'redoCount',
      sorter: (a, b) => compareNumber(a.redoCount, b.redoCount),
    },
    {
      title: 'Retry Exhausted',
      dataIndex: 'retryExhaustedCount',
      key: 'retryExhaustedCount',
      sorter: (a, b) => compareNumber(a.retryExhaustedCount, b.retryExhaustedCount),
    },
    {
      title: 'Finalize Fail',
      dataIndex: 'chapterFinalizeFailureCount',
      key: 'chapterFinalizeFailureCount',
      sorter: (a, b) => compareNumber(a.chapterFinalizeFailureCount, b.chapterFinalizeFailureCount),
    },
    {
      title: 'Est. Cost',
      dataIndex: 'estimatedCostUsd',
      key: 'estimatedCostUsd',
      render: (v) => usd(v),
      sorter: (a, b) => compareNumber(a.estimatedCostUsd, b.estimatedCostUsd),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, row) =>
        row.runId ? (
          <Button size="small" onClick={() => applyRunFilter(row.runId)}>
            Filter Run
          </Button>
        ) : null,
    },
  ];

  const eventColumns: ColumnsType<EventItem> = [
    {
      title: 'Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (value?: string) => (value ? new Date(value).toLocaleString() : '—'),
      sorter: (a, b) => compareDate(a.timestamp, b.timestamp),
    },
    {
      title: 'Stage',
      dataIndex: 'stage',
      key: 'stage',
      sorter: (a, b) => compareText(a.stage, b.stage),
    },
    {
      title: 'Outcome',
      dataIndex: 'outcome',
      key: 'outcome',
      sorter: (a, b) => compareText(a.outcome, b.outcome),
    },
    {
      title: 'Code',
      dataIndex: 'outcomeCode',
      key: 'outcomeCode',
      sorter: (a, b) => compareText(a.outcomeCode, b.outcomeCode),
    },
    {
      title: 'Book',
      dataIndex: 'bookId',
      key: 'bookId',
      sorter: (a, b) => compareText(a.bookId, b.bookId),
    },
    {
      title: 'Chapter',
      dataIndex: 'chapterGuid',
      key: 'chapterGuid',
      sorter: (a, b) => compareText(a.chapterGuid, b.chapterGuid),
    },
    {
      title: 'Model',
      dataIndex: 'configuredModelId',
      key: 'configuredModelId',
      sorter: (a, b) => compareText(a.configuredModelId, b.configuredModelId),
    },
    {
      title: 'Language',
      key: 'language',
      render: (_, row) => `${row.sourceLang || '—'} -> ${row.targetLang || '—'}`,
      sorter: (a, b) =>
        compareText(`${a.sourceLang || ''}->${a.targetLang || ''}`, `${b.sourceLang || ''}->${b.targetLang || ''}`),
    },
    {
      title: 'Attempt',
      dataIndex: 'attemptNumber',
      key: 'attemptNumber',
      sorter: (a, b) => compareNumber(a.attemptNumber, b.attemptNumber),
    },
    {
      title: 'Est. Cost',
      dataIndex: 'estimatedCostUsd',
      key: 'estimatedCostUsd',
      render: (v) => usd(v),
      sorter: (a, b) => compareNumber(a.estimatedCostUsd, b.estimatedCostUsd),
    },
    {
      title: 'Reason',
      dataIndex: 'reasonText',
      key: 'reasonText',
      ellipsis: true,
      sorter: (a, b) => compareText(a.reasonText, b.reasonText),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, row) => (
        <Space size={4}>
          {row.runId ? (
            <Button
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                applyRunFilter(row.runId);
              }}
            >
              Filter Run
            </Button>
          ) : null}
          {row.bookId && row.chapterGuid ? (
            <Button
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                applyChapterFilter(row.bookId, row.chapterGuid);
              }}
            >
              Filter Chapter
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  const timelineColumns: ColumnsType<EventItem> = [
    {
      title: 'Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (value?: string) => (value ? new Date(value).toLocaleString() : '—'),
      sorter: (a, b) => compareDate(a.timestamp, b.timestamp),
    },
    {
      title: 'Stage',
      dataIndex: 'stage',
      key: 'stage',
      sorter: (a, b) => compareText(a.stage, b.stage),
    },
    {
      title: 'Outcome',
      dataIndex: 'outcome',
      key: 'outcome',
      sorter: (a, b) => compareText(a.outcome, b.outcome),
    },
    {
      title: 'Code',
      dataIndex: 'outcomeCode',
      key: 'outcomeCode',
      sorter: (a, b) => compareText(a.outcomeCode, b.outcomeCode),
    },
    {
      title: 'Attempt',
      dataIndex: 'attemptNumber',
      key: 'attemptNumber',
      sorter: (a, b) => compareNumber(a.attemptNumber, b.attemptNumber),
    },
    {
      title: 'Model',
      dataIndex: 'configuredModelId',
      key: 'configuredModelId',
      sorter: (a, b) => compareText(a.configuredModelId, b.configuredModelId),
    },
    {
      title: 'Reason',
      dataIndex: 'reasonText',
      key: 'reasonText',
      ellipsis: true,
      sorter: (a, b) => compareText(a.reasonText, b.reasonText),
    },
  ];

  const hasDrilldownFilters = Boolean(filters.run_id || filters.book_id || filters.chapter_guid);

  return (
    <Space className="admin-dashboard-surface admin-reliability-dashboard" direction="vertical" size={16} style={{ width: '100%' }}>
      <Card className="admin-reliability-toolbar-card">
        <Space wrap size={12}>
          <Space direction="vertical" size={0}>
            <Text strong>Days</Text>
            <Select
              value={filters.days}
              style={{ width: 110 }}
              onChange={(value) => setFilters((prev) => ({ ...prev, days: value }))}
            >
              <Option value={7}>7</Option>
              <Option value={14}>14</Option>
              <Option value={30}>30</Option>
            </Select>
          </Space>
          <Space direction="vertical" size={0}>
            <Text strong>Source</Text>
            <Select
              allowClear
              value={filters.source_lang}
              style={{ width: 180 }}
              onChange={(value) => setFilters((prev) => ({ ...prev, source_lang: value }))}
            >
              {(filterOptions?.sourceLangs || []).map((item) => (
                <Option key={item} value={item}>{item}</Option>
              ))}
            </Select>
          </Space>
          <Space direction="vertical" size={0}>
            <Text strong>Target</Text>
            <Select
              allowClear
              value={filters.target_lang}
              style={{ width: 180 }}
              onChange={(value) => setFilters((prev) => ({ ...prev, target_lang: value }))}
            >
              {(filterOptions?.targetLangs || []).map((item) => (
                <Option key={item} value={item}>{item}</Option>
              ))}
            </Select>
          </Space>
          <Space direction="vertical" size={0}>
            <Text strong>Stage</Text>
            <Select
              allowClear
              value={filters.stage}
              style={{ width: 220 }}
              onChange={(value) => setFilters((prev) => ({ ...prev, stage: value }))}
            >
              {(filterOptions?.stages || []).map((item) => (
                <Option key={item} value={item}>{item}</Option>
              ))}
            </Select>
          </Space>
          <Space direction="vertical" size={0}>
            <Text strong>Outcome</Text>
            <Select
              allowClear
              value={filters.outcome}
              style={{ width: 180 }}
              onChange={(value) => setFilters((prev) => ({ ...prev, outcome: value }))}
            >
              {(filterOptions?.outcomes || []).map((item) => (
                <Option key={item} value={item}>{item}</Option>
              ))}
            </Select>
          </Space>
          <Space direction="vertical" size={0}>
            <Text strong>Outcome Code</Text>
            <Select
              allowClear
              value={filters.outcome_code}
              style={{ width: 260 }}
              onChange={(value) => setFilters((prev) => ({ ...prev, outcome_code: value }))}
            >
              {(filterOptions?.outcomeCodes || []).map((item) => (
                <Option key={item} value={item}>{item}</Option>
              ))}
            </Select>
          </Space>
          <Space direction="vertical" size={0}>
            <Text strong>Provider</Text>
            <Select
              allowClear
              value={filters.provider}
              style={{ width: 180 }}
              onChange={(value) => setFilters((prev) => ({ ...prev, provider: value }))}
            >
              {(filterOptions?.providers || []).map((item) => (
                <Option key={item} value={item}>{item}</Option>
              ))}
            </Select>
          </Space>
          <Space direction="vertical" size={0}>
            <Text strong>Model</Text>
            <Select
              allowClear
              value={filters.model}
              style={{ width: 220 }}
              onChange={(value) => setFilters((prev) => ({ ...prev, model: value }))}
            >
              {(filterOptions?.models || []).map((item) => (
                <Option key={item} value={item}>{item}</Option>
              ))}
            </Select>
          </Space>
          <Space direction="vertical" size={0}>
            <Text strong>Run</Text>
            <Input
              allowClear
              value={filters.run_id}
              placeholder="run id"
              style={{ width: 220 }}
              onChange={(event) => setFilters((prev) => ({ ...prev, run_id: event.target.value || undefined }))}
            />
          </Space>
          <Space direction="vertical" size={0}>
            <Text strong>Book</Text>
            <Input
              allowClear
              value={filters.book_id}
              placeholder="book id"
              style={{ width: 220 }}
              onChange={(event) => setFilters((prev) => ({ ...prev, book_id: event.target.value || undefined }))}
            />
          </Space>
          <Space direction="vertical" size={0}>
            <Text strong>Chapter</Text>
            <Input
              allowClear
              value={filters.chapter_guid}
              placeholder="chapter guid"
              style={{ width: 260 }}
              onChange={(event) => setFilters((prev) => ({ ...prev, chapter_guid: event.target.value || undefined }))}
            />
          </Space>
          <Space>
            <Button type="primary" onClick={loadDashboard} loading={loading}>Refresh</Button>
            <Button onClick={handleRebuild} loading={rebuilding}>Rebuild Aggregates</Button>
            {hasDrilldownFilters ? (
              <Button onClick={clearDrilldownFilters}>Clear Drilldown</Button>
            ) : null}
            <Popconfirm
              title="Delete all translation outcome data?"
              description="This permanently deletes events, daily aggregates, and rebuild records."
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true, loading: deleting }}
              onConfirm={handleDeleteEvents}
            >
              <Button danger loading={deleting}>Delete Events</Button>
            </Popconfirm>
          </Space>
        </Space>
      </Card>

      {dashboardNotice && (
        <Alert
          className="admin-dashboard-inline-alert"
          type="warning"
          showIcon
          message="Reliability dashboard is partially refreshed"
          description={dashboardNotice}
          action={
            <Button size="small" onClick={() => void loadDashboard()} loading={loading}>
              Retry
            </Button>
          }
        />
      )}

      <Row gutter={12} className="admin-reliability-overview-band">
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}><Statistic title="Terminal Failure Rate" value={percent(summary?.failureRate)} /></Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="Post-Translate REDO Rate"
              value={percent(summary?.alignmentPostTranslateRedoRate ?? summary?.alignmentRedoRate)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="Final Alignment REDO Rate"
              value={percent(summary?.alignmentFinalRedoRate ?? summary?.alignmentRedoRate)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}><Statistic title="Proofreader Retry Rate" value={percent(summary?.proofreaderRetryRate)} /></Card>
        </Col>
      </Row>

      <Row gutter={12} className="admin-reliability-overview-band">
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}><Statistic title="Retry Recovery Rate" value={percent(summary?.retryRecoveryRate)} /></Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}><Statistic title="Retry Cost Share" value={percent(summary?.retryCostShare)} /></Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}><Statistic title="Retry Cost (Est.)" value={usd(summary?.retryCostUsd)} /></Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}><Statistic title="Failure/Redo Cost (Est.)" value={usd(summary?.failureLikeCostUsd)} /></Card>
        </Col>
      </Row>

      <Row gutter={12} className="admin-reliability-overview-band">
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}><Statistic title="Retry Scheduled" value={summary?.retryScheduledCount || 0} /></Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}><Statistic title="Proofreader Retries" value={summary?.proofreaderRetryScheduledCount || 0} /></Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}><Statistic title="Retry Exhausted" value={summary?.retryExhaustedCount || 0} /></Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}><Statistic title="Total Est. Cost" value={usd(summary?.estimatedCostUsd)} /></Card>
        </Col>
      </Row>

      <Row gutter={12} className="admin-reliability-overview-band">
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}>
            <Statistic title="Impacted Users" value={breakdown?.customerImpact?.impactedUserCount || 0} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}>
            <Statistic title="Impacted Books" value={breakdown?.customerImpact?.impactedBookCount || 0} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="Finalize Failure Rate"
              value={percent(breakdown?.customerImpact?.chapterFinalizeFailureRate)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="Recovered After REDO"
              value={percent(breakdown?.failureFunnel?.recoveredAfterAlignmentRedoRate)}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={12} className="admin-reliability-overview-band">
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}>
            <Statistic title="Alignment REDO (Runs)" value={breakdown?.failureFunnel?.alignmentRedoCount || 0} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}>
            <Statistic title="Finalize Fail (Runs)" value={breakdown?.failureFunnel?.chapterFinalizeFailureCount || 0} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="Retry Exhausted, No Success"
              value={breakdown?.failureFunnel?.retryExhaustedWithoutSuccessCount || 0}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card loading={loading}>
            <Statistic
              title="Thin Failure Reasons"
              value={percent(breakdown?.falsePositiveSignals?.thinReasonTextRate)}
            />
          </Card>
        </Col>
      </Row>

      <Card title="Failure Funnel Snapshot" loading={loading}>
        <Descriptions size="small" column={2}>
          <Descriptions.Item label="Tracked Runs/Chapters">{breakdown?.failureFunnel?.trackedRunOrChapterCount || 0}</Descriptions.Item>
          <Descriptions.Item label="Finalize Success">{breakdown?.failureFunnel?.chapterFinalizeSuccessCount || 0}</Descriptions.Item>
          <Descriptions.Item label="Alignment Final REDO">{breakdown?.failureFunnel?.alignmentFinalRedoCount || 0}</Descriptions.Item>
          <Descriptions.Item label="Finalize Alignment Fail">{breakdown?.failureFunnel?.chapterFinalizeAlignmentFailureCount || 0}</Descriptions.Item>
          <Descriptions.Item label="Finalize Provider Rejected">{breakdown?.failureFunnel?.chapterFinalizeProviderRejectedCount || 0}</Descriptions.Item>
          <Descriptions.Item label="Retry Exhausted Then Success">{breakdown?.failureFunnel?.retryExhaustedThenSuccessCount || 0}</Descriptions.Item>
          <Descriptions.Item label="Finalize Fail Then Success">{breakdown?.failureFunnel?.finalizeFailureThenSuccessCount || 0}</Descriptions.Item>
          <Descriptions.Item label="Alignment REDO Recovered">{breakdown?.falsePositiveSignals?.alignmentRedoRecoveredCount || 0}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Daily Trends" loading={loading} className="admin-reliability-chart-card">
        {trends.length === 0 ? (
          <Alert type="info" message="No trend data for selected filters." />
        ) : (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div className="admin-reliability-chart-scroll">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={trends}>
                <CartesianGrid stroke={RELIABILITY_CHART_COLORS.grid} strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="successCount" stackId="outcome" fill={RELIABILITY_CHART_COLORS.success} name="Success" />
                <Bar dataKey="failureCount" stackId="outcome" fill={RELIABILITY_CHART_COLORS.danger} name="Failure" />
                <Bar dataKey="redoCount" stackId="outcome" fill={RELIABILITY_CHART_COLORS.warning} name="REDO" />
                <Bar dataKey="retryScheduledCount" stackId="outcome" fill={RELIABILITY_CHART_COLORS.teal} name="Retry Scheduled" />
              </BarChart>
            </ResponsiveContainer>
            </div>
            <div className="admin-reliability-chart-scroll">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={trendRateSeries}>
                <CartesianGrid stroke={RELIABILITY_CHART_COLORS.grid} strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis unit="%" />
                <Tooltip formatter={(value: number) => `${Number(value || 0).toFixed(2)}%`} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="alignmentPostTranslateRedoRatePct"
                  stroke={RELIABILITY_CHART_COLORS.warning}
                  name="Post-Translate REDO Rate"
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="alignmentFinalRedoRatePct"
                  stroke={RELIABILITY_CHART_COLORS.info}
                  name="Final Alignment REDO Rate"
                  dot={false}
                />
                <Line type="monotone" dataKey="proofreaderRetryRatePct" stroke={RELIABILITY_CHART_COLORS.teal} name="Proofreader Retry Rate" dot={false} />
              </LineChart>
            </ResponsiveContainer>
            </div>
          </Space>
        )}
      </Card>

      <Card title="Stage Reliability" loading={loading}>
        <Table
          rowKey={(row) => row.stage}
          dataSource={breakdown?.byStage || []}
          columns={stageColumns}
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </Card>

      <Card title="Model + Language Breakdown" loading={loading}>
        <Table
          rowKey={(row) => `${row.configuredModelId}|${row.provider}|${row.sourceLang}|${row.targetLang}`}
          dataSource={breakdown?.byModelLanguage || []}
          columns={modelColumns}
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </Card>

      <Card title="Top Outcome Codes" loading={loading}>
        <Table
          rowKey={(row) => row.outcomeCode}
          dataSource={breakdown?.byReason || []}
          columns={reasonColumns}
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </Card>

      <Card title="Reason Buckets" loading={loading}>
        <Table
          rowKey={(row) => row.bucket}
          dataSource={breakdown?.byReasonBucket || []}
          columns={reasonBucketColumns}
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </Card>

      <Card title="Model Outliers" loading={loading}>
        <Table
          rowKey={(row) => `${row.configuredModelId}|${row.provider}|${row.sourceLang}|${row.targetLang}`}
          dataSource={breakdown?.modelOutliers || []}
          columns={modelOutlierColumns}
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </Card>

      <Card title="Regression Alerts" loading={loading}>
        {(breakdown?.regressionAlerts || []).length === 0 ? (
          <Alert type="success" message="No recent regression alerts in the selected range." />
        ) : (
          <Table
            rowKey={(row) => `${row.metric}_${row.latestDay}`}
            dataSource={breakdown?.regressionAlerts || []}
            columns={regressionAlertColumns}
            pagination={{ pageSize: 10 }}
            size="small"
          />
        )}
      </Card>

      <Card title="Chapter Hotspots" loading={loading}>
        <Table
          rowKey={(row) => `${row.bookId}|${row.chapterGuid}`}
          dataSource={breakdown?.chapterHotspots || []}
          columns={chapterHotspotColumns}
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </Card>

      <Card title="Run Hotspots" loading={loading}>
        <Table
          rowKey={(row) => row.runId}
          dataSource={breakdown?.runHotspots || []}
          columns={runHotspotColumns}
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </Card>

      <Row gutter={12}>
        <Col xs={24} lg={12}>
          <Card title="Top Impacted Users" loading={loading}>
            <Table
              rowKey={(row) => row.userId}
              dataSource={breakdown?.customerImpact?.topImpactedUsers || []}
              pagination={{ pageSize: 8 }}
              size="small"
              columns={[
                { title: 'User', dataIndex: 'userId', key: 'userId', sorter: (a, b) => compareText(a.userId, b.userId) },
                { title: 'Finalize Fail', dataIndex: 'chapterFinalizeFailures', key: 'chapterFinalizeFailures', sorter: (a, b) => compareNumber(a.chapterFinalizeFailures, b.chapterFinalizeFailures) },
                { title: 'Retry Exhausted', dataIndex: 'retryExhaustedCount', key: 'retryExhaustedCount', sorter: (a, b) => compareNumber(a.retryExhaustedCount, b.retryExhaustedCount) },
                { title: 'Chapters', dataIndex: 'affectedChapters', key: 'affectedChapters', sorter: (a, b) => compareNumber(a.affectedChapters, b.affectedChapters) },
                { title: 'Est. Cost', dataIndex: 'estimatedCostUsd', key: 'estimatedCostUsd', render: (v: number) => usd(v), sorter: (a, b) => compareNumber(a.estimatedCostUsd, b.estimatedCostUsd) },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Top Impacted Books" loading={loading}>
            <Table
              rowKey={(row) => row.bookId}
              dataSource={breakdown?.customerImpact?.topImpactedBooks || []}
              pagination={{ pageSize: 8 }}
              size="small"
              columns={[
                { title: 'Book', dataIndex: 'bookId', key: 'bookId', sorter: (a, b) => compareText(a.bookId, b.bookId) },
                { title: 'Finalize Fail', dataIndex: 'chapterFinalizeFailures', key: 'chapterFinalizeFailures', sorter: (a, b) => compareNumber(a.chapterFinalizeFailures, b.chapterFinalizeFailures) },
                { title: 'Retry Exhausted', dataIndex: 'retryExhaustedCount', key: 'retryExhaustedCount', sorter: (a, b) => compareNumber(a.retryExhaustedCount, b.retryExhaustedCount) },
                { title: 'Users', dataIndex: 'affectedUsers', key: 'affectedUsers', sorter: (a, b) => compareNumber(a.affectedUsers, b.affectedUsers) },
                { title: 'Est. Cost', dataIndex: 'estimatedCostUsd', key: 'estimatedCostUsd', render: (v: number) => usd(v), sorter: (a, b) => compareNumber(a.estimatedCostUsd, b.estimatedCostUsd) },
              ]}
            />
          </Card>
        </Col>
      </Row>

      <Card title="Recent Events" loading={loading}>
        <Table
          rowKey={(row, idx) => row.id || `${row.timestamp || 'ts'}_${idx}`}
          dataSource={events}
          columns={eventColumns}
          pagination={{ pageSize: 20 }}
          size="small"
          scroll={{ x: 1600 }}
          onRow={(row) => ({
            onClick: () => setSelectedEvent(row),
          })}
        />
      </Card>

      <Card>
        <Title level={5} style={{ marginBottom: 6 }}>Metric Definitions</Title>
        {Object.keys(metricDefinitions).length === 0 ? (
          <Text type="secondary">Definitions unavailable.</Text>
        ) : (
          <Descriptions size="small" column={1}>
            {Object.entries(metricDefinitions).map(([key, value]) => (
              <Descriptions.Item key={key} label={key}>{value}</Descriptions.Item>
            ))}
          </Descriptions>
        )}
      </Card>

      <Drawer
        open={Boolean(selectedEvent)}
        title="Outcome Event"
        onClose={() => setSelectedEvent(null)}
        width={960}
      >
        {selectedEvent ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Time">{selectedEvent.timestamp ? new Date(selectedEvent.timestamp).toLocaleString() : '—'}</Descriptions.Item>
              <Descriptions.Item label="Stage">{selectedEvent.stage || '—'}</Descriptions.Item>
              <Descriptions.Item label="Outcome">{selectedEvent.outcome || '—'}</Descriptions.Item>
              <Descriptions.Item label="Code">{selectedEvent.outcomeCode || '—'}</Descriptions.Item>
              <Descriptions.Item label="Reason">{selectedEvent.reasonText || '—'}</Descriptions.Item>
              <Descriptions.Item label="Run">{selectedEvent.runId || '—'}</Descriptions.Item>
              <Descriptions.Item label="Book">{selectedEvent.bookId || '—'}</Descriptions.Item>
              <Descriptions.Item label="Chapter">{selectedEvent.chapterGuid || '—'}</Descriptions.Item>
              <Descriptions.Item label="Chunk">{selectedEvent.chunkId ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Model">{selectedEvent.configuredModelId || '—'}</Descriptions.Item>
              <Descriptions.Item label="Provider">{selectedEvent.provider || '—'}</Descriptions.Item>
              <Descriptions.Item label="Language">{`${selectedEvent.sourceLang || '—'} -> ${selectedEvent.targetLang || '—'}`}</Descriptions.Item>
              <Descriptions.Item label="Attempt">{selectedEvent.attemptNumber ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Est. Cost">{usd(selectedEvent.estimatedCostUsd)}</Descriptions.Item>
            </Descriptions>
            <Space wrap>
              {selectedEvent.runId ? (
                <Button onClick={() => applyRunFilter(selectedEvent.runId)}>Filter Dashboard To Run</Button>
              ) : null}
              {selectedEvent.bookId && selectedEvent.chapterGuid ? (
                <Button onClick={() => applyChapterFilter(selectedEvent.bookId, selectedEvent.chapterGuid)}>
                  Filter Dashboard To Chapter
                </Button>
              ) : null}
              {selectedEvent.runId ? (
                <Button
                  href={`${API_BASE}/api/admin/ai-logs?run_id=${encodeURIComponent(selectedEvent.runId)}`}
                  target="_blank"
                >
                  Open AI Logs
                </Button>
              ) : null}
              {selectedEvent.runId ? (
                <Button
                  href={`${API_BASE}/api/admin/translation-costs?run_id=${encodeURIComponent(selectedEvent.runId)}`}
                  target="_blank"
                >
                  Open Translation Costs
                </Button>
              ) : null}
            </Space>
            <Card title="Run/Chapter Timeline" size="small">
              <Table
                rowKey={(row, idx) => row.id || `${row.timestamp || 'ts'}_${idx}`}
                dataSource={timelineEvents}
                columns={timelineColumns}
                loading={timelineLoading}
                pagination={{ pageSize: 8 }}
                size="small"
                scroll={{ x: 900 }}
              />
            </Card>
          </Space>
        ) : null}
      </Drawer>
    </Space>
  );
};

export default TranslationReliabilityTab;
